import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Card,
  Divider,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../src/utils/api";

import EventNoteIcon from "@mui/icons-material/EventNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import CommentIcon from "@mui/icons-material/Comment";
import AddCommentIcon from "@mui/icons-material/AddComment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

const Visits = ({ violation_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    backgroundColor: "#fff",
  };

  const softInsetSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const sectionIconWrapSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`/getEvents`);
        const filteredEvents = response.data.filter(
          (event) => event.violation_id === Number(violation_id)
        );
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (violation_id) {
      fetchEvents();
    }
  }, [violation_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(`/getViolationComments/${violation_id}/${eventId}`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    fetchComments(eventId);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("violation_id", violation_id);
      formData.append("event_id", selectedEventId);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", 0);

      if (selectedFile) {
        formData.append("upload_urls", selectedFile);
      }

      await api.post(`/addViolationComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setComments((prev) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
          upload_urls: selectedFile ? selectedFile.name : null,
        },
        ...prev,
      ]);

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  const openAttachmentPreview = (src) => {
    setPreviewImageUrl(src);
    setOpenImagePreview(true);
  };

  return (
    <Box>
     

      {loading ? (
        <Box
          sx={{
            minHeight: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
            No visits found
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
            There are no visits related to this violation yet.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {events.map((event) => (
            <Card key={event.id} sx={{ ...softCardSx, p: 2.25 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
                  >
                    {event.title || `Visit #${event.id}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                    Event ID: {event.id} • Violation ID: {event.violation_id}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<AddCommentIcon />}
                  onClick={() => handleOpenComments(event.id)}
                  sx={primaryButtonSx}
                >
                  View/Add Comments
                </Button>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                }}
              >
                <Box sx={{ ...softInsetSx, p: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box sx={sectionIconWrapSx}>
                      <DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#223142",
                          fontWeight: 600,
                          wordBreak: "break-word",
                        }}
                      >
                        {event.description || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ ...softInsetSx, p: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box sx={sectionIconWrapSx}>
                      <AccessTimeIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                      >
                        Start Time
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                        {new Date(event.start_time).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ ...softInsetSx, p: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box sx={sectionIconWrapSx}>
                      <EventNoteIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                      >
                        End Time
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                        {new Date(event.end_time).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

              
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 720 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Event Comments
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                Violation ID: {violation_id} • Event ID: {selectedEventId}
              </Typography>
            </Box>

            <IconButton onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                <CommentIcon sx={{ color: "#2f63ad" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                  Existing Comments
                </Typography>
              </Box>

              {isCommentsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : comments.length > 0 ? (
                <Stack spacing={2}>
                  {comments.map((comment, index) => {
                    const imageSrc = comment.upload_urls
                      ? `${API_BASE_URL}/violationsimages/${encodeURIComponent(
                          comment.upload_urls?.split(/(\\|\/)/g).pop()
                        )}`
                      : null;

                    return (
                      <Card
                        key={index}
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                          backgroundColor: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid rgba(47,99,173,0.08)",
                            backgroundColor: "rgba(47,99,173,0.05)",
                          }}
                        >
                          <Chip
                            label="Event Comment"
                            size="small"
                            sx={{
                              fontWeight: 700,
                              borderRadius: "999px",
                              color: "#1d4ed8",
                              backgroundColor: "rgba(59,130,246,0.12)",
                            }}
                          />
                        </Box>

                        <Box sx={{ p: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#223142",
                              whiteSpace: "pre-wrap",
                              mb: imageSrc ? 1.25 : 0,
                            }}
                          >
                            {comment.comment_text}
                          </Typography>

                          {imageSrc && (
                            <Box sx={{ mt: 1.25 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                              >
                                Attachment
                              </Typography>

                              <Card
                                sx={{
                                  ...softInsetSx,
                                  p: 2,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                  textAlign: "center",
                                  maxWidth: 260,
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={imageSrc}
                                  alt="Comment Attachment"
                                  sx={{
                                    maxWidth: "100%",
                                    maxHeight: 120,
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    objectFit: "cover",
                                  }}
                                  onClick={() => openAttachmentPreview(imageSrc)}
                                />

                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.5, wordBreak: "break-word" }}
                                >
                                  Attachment Preview
                                </Typography>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => openAttachmentPreview(imageSrc)}
                                  sx={secondaryButtonSx}
                                >
                                  Preview
                                </Button>
                              </Card>
                            </Box>
                          )}

                          <Divider sx={{ my: 1.5, borderColor: "rgba(47,99,173,0.08)" }} />

                          <Typography
                            variant="caption"
                            sx={{
                              color: "#6b778c",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14 }} />
                            Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                            {new Date(comment.sent_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                <Card sx={{ ...softInsetSx, p: 2.5, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    No comments available for this event.
                  </Typography>
                </Card>
              )}
            </Box>

            <Divider sx={{ mb: 3, borderColor: "rgba(47,99,173,0.08)" }} />

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                <AddCommentIcon sx={{ color: "#2f63ad" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                  Add Comment
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Comment"
                multiline
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                sx={{ ...textFieldSx, mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600, color: "#223142" }}
                >
                  Attach Image
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    ...softInsetSx,
                    p: 2.5,
                    textAlign: "center",
                    cursor: "pointer",
                    borderStyle: "dashed",
                    borderWidth: "2px",
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Drag & drop an image, or click to select
                  </Typography>
                </Box>
              </Box>

              {filePreview && (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: "#223142" }}>
                    Preview
                  </Typography>
                  <img
                    src={filePreview}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8 }}
                  />
                </Box>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsModalOpen(false)}
                  sx={secondaryButtonSx}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  sx={primaryButtonSx}
                >
                  Save Comment
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={openImagePreview} onClose={() => setOpenImagePreview(false)}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 1000,
              maxHeight: "90vh",
            }}
          >
            <IconButton
              onClick={() => setOpenImagePreview(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                bgcolor: "rgba(0,0,0,0.45)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>

            <Box
              component="img"
              src={previewImageUrl}
              alt="Attachment Preview"
              sx={{
                width: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "12px",
                display: "block",
                mx: "auto",
                backgroundColor: "#111",
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Visits;